import { useState, useEffect, type FormEvent } from 'react';
import { generateClient } from 'aws-amplify/api';
import { updatePassword } from 'aws-amplify/auth';
import { useAuth } from '../providers/AuthProvider';
import { useTheme } from '../providers/ThemeProvider';
import { getUserProfile, updateUserProfile } from '../graphql/profile';
import type { UserProfile, UserProfileFormData, PasswordChangeFormData, UnitSystem } from '../types/profile';

const client = generateClient();

const emptyProfileForm: UserProfileFormData = {
  name: '',
  phone: '',
  preferredUnits: 'US',
  city: '',
  state: '',
  country: '',
};

const emptyPasswordForm: PasswordChangeFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export function UserProfilePage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [profileForm, setProfileForm] = useState<UserProfileFormData>(emptyProfileForm);
  const [passwordForm, setPasswordForm] = useState<PasswordChangeFormData>(emptyPasswordForm);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (!user) return;
    loadProfile();
  }, [user]);

  async function loadProfile() {
    setIsLoadingProfile(true);
    try {
      const result = await client.graphql({
        query: getUserProfile,
        variables: { userId: user!.userId },
      }) as { data: { getUserProfile: UserProfile | null } };

      const profile = result.data.getUserProfile;
      if (profile) {
        setProfileForm({
          name: profile.name || '',
          phone: profile.phone || '',
          preferredUnits: profile.preferredUnits || 'US',
          city: profile.city || '',
          state: profile.state || '',
          country: profile.country || '',
        });
      }
    } catch {
      setProfileError('Failed to load profile.');
    } finally {
      setIsLoadingProfile(false);
    }
  }

  async function handleProfileSubmit(e: FormEvent) {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');
    setIsSavingProfile(true);

    try {
      await client.graphql({
        query: updateUserProfile,
        variables: {
          input: {
            userId: user!.userId,
            name: profileForm.name.trim(),
            phone: profileForm.phone.trim(),
            preferredUnits: profileForm.preferredUnits,
            city: profileForm.city.trim(),
            state: profileForm.state.trim(),
            country: profileForm.country.trim(),
          },
        },
      });
      setProfileMessage('Profile updated successfully.');
    } catch {
      setProfileError('Failed to save profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setPasswordError('');
    setPasswordMessage('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    setIsChangingPassword(true);
    try {
      await updatePassword({
        oldPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordMessage('Password changed successfully.');
      setPasswordForm(emptyPasswordForm);
    } catch (err) {
      setPasswordError(
        err instanceof Error ? err.message : 'Failed to change password. Please try again.'
      );
    } finally {
      setIsChangingPassword(false);
    }
  }

  function handleProfileChange(field: keyof UserProfileFormData, value: string) {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  }

  function handlePasswordChange(field: keyof PasswordChangeFormData, value: string) {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
  }

  if (isLoadingProfile) {
    return (
      <div className="page profile-page">
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="page profile-page">
      <h2>User Profile</h2>

      {/* Profile Info Section */}
      <section className="profile-section">
        <h3>Personal Information</h3>
        <form onSubmit={handleProfileSubmit} className="profile-form">
          {profileError && (
            <div className="error-message" role="alert">{profileError}</div>
          )}
          {profileMessage && (
            <div className="success-message" role="status">{profileMessage}</div>
          )}

          <div className="form-group">
            <label htmlFor="profile-name">Name</label>
            <input
              id="profile-name"
              type="text"
              value={profileForm.name}
              onChange={(e) => handleProfileChange('name', e.target.value)}
              maxLength={100}
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="profile-phone">Phone Number</label>
            <input
              id="profile-phone"
              type="tel"
              value={profileForm.phone}
              onChange={(e) => handleProfileChange('phone', e.target.value)}
              maxLength={20}
              autoComplete="tel"
            />
          </div>

          <div className="form-group">
            <label htmlFor="profile-units">Preferred Units</label>
            <select
              id="profile-units"
              value={profileForm.preferredUnits}
              onChange={(e) => handleProfileChange('preferredUnits', e.target.value as UnitSystem)}
            >
              <option value="US">US (oz, lb, °F)</option>
              <option value="metric">Metric (g, kg, °C)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="profile-city">City</label>
            <input
              id="profile-city"
              type="text"
              value={profileForm.city}
              onChange={(e) => handleProfileChange('city', e.target.value)}
              maxLength={100}
              autoComplete="address-level2"
            />
          </div>

          <div className="form-group">
            <label htmlFor="profile-state">State / Province</label>
            <input
              id="profile-state"
              type="text"
              value={profileForm.state}
              onChange={(e) => handleProfileChange('state', e.target.value)}
              maxLength={100}
              autoComplete="address-level1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="profile-country">Country</label>
            <input
              id="profile-country"
              type="text"
              value={profileForm.country}
              onChange={(e) => handleProfileChange('country', e.target.value)}
              maxLength={100}
              autoComplete="country-name"
            />
          </div>

          <button type="submit" className="cta-button" disabled={isSavingProfile}>
            {isSavingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </section>

      {/* Change Password Section */}
      <section className="profile-section">
        <h3>Change Password</h3>
        <form onSubmit={handlePasswordSubmit} className="profile-form">
          {passwordError && (
            <div className="error-message" role="alert">{passwordError}</div>
          )}
          {passwordMessage && (
            <div className="success-message" role="status">{passwordMessage}</div>
          )}

          <div className="form-group">
            <label htmlFor="current-password">Current Password</label>
            <input
              id="current-password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>

          <button type="submit" className="cta-button" disabled={isChangingPassword}>
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </section>

      {/* Appearance Section */}
      <section className="profile-section">
        <h3>Appearance</h3>
        <div className="theme-toggle-row">
          <span>Theme</span>
          <button
            onClick={toggleTheme}
            className="theme-toggle-button"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
                <span>Switch to Dark Mode</span>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
                <span>Switch to Light Mode</span>
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}
